import { createApp } from 'vue'

import './styles/main.scss'

import { Navbar, Menu, Sidebar, Snackbar, Modal, Icon, Carousel, Switch, Dialog, Notification, Tabs, Table, Button, Tooltip, Select, Input, Checkbox, Dropdown, Field, Upload, Loading } from 'buefy'

import { findLastIndex, get } from 'lodash'

import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import SiteTitleWatcherPlugin from '@/stores/plugins/SiteTitleWatcher'

import { createRouter, createWebHistory } from 'vue-router'
import App from './App.vue'

import MapApp from './MapApp.vue'

import FlowModal from '@/components/FlowModal.vue'
import ConfirmAddressBanner from '@/components/lead_workflow_components/ConfirmAddressBanner.vue'
import OutlineAreasBanner from '@/components/lead_workflow_components/OutlineAreasBanner.vue'
import MapResultsBanner from '@/components/lead_workflow_components/MapResultsBanner.vue'
import MapEditBanner from '@/components/lead_workflow_components/MapEditBanner.vue'

import { registerIcons } from '@/icons'

const router = createRouter({
  history: createWebHistory(),
  routes: [{
  path: '/',
  redirect: to => ({
    path: '/flow/introduction',
    query: to.query  // Preserve query params during redirect
  }),
  name: 'Introduction'
},{
  name: 'OutlineAreas',
  path: '/flow/outline-areas',
  meta: {
    viewMode: 'edit',
    showControlPanel: true,
    showStatusPanel: true,
  },
  components: {
    default: MapApp,
    toolbar: OutlineAreasBanner
  }
},{
  name: 'ConfirmAddress',
  path: '/flow/confirm-address',
  components: {
    default: MapApp,
    toolbar: ConfirmAddressBanner
  }
},{
  name: 'Flow',
  path: '/flow/:stepName',
  components: {
    default: MapApp,
    dialog: FlowModal
  },
  props: {
    dialog: { firstStepName: 'Introduction'}
  }
},{
  name: 'MapResults',
  path: '/map/:mapId/results',
  meta: {
    viewMode: 'view',
    showMeasurements: true,
    showStatusPanel: true,
    showSharingDetails: true
  },
  components: {
    default: MapApp,
    toolbar: MapResultsBanner
  }
},{
  name: 'MapResultsEdit',
  path: '/map/:mapId/edit',
  meta: {
    viewMode: 'edit',
    showControlPanel: true,
    showControlPanelSearchButton: false,
    showMeasurements: true,
    showStatusPanel: true,
    showSharingDetails: true,    
  },
  components: {
    default: MapApp,
    toolbar: MapEditBanner
  }
},{
  name: 'MapScreenshot',
  path: '/map/:mapId/screenshot',
  meta: {
    viewMode: 'view',
    showMeasurements: true,
    showStatusPanel: false,
    showSharingDetails: false
  },
  components: {
    default: MapApp,
    toolbar: MapResultsBanner
  }
}]
});

router.beforeEach(async (to, from, next) => {
  const { useBackendStore } = await import('./stores/backend')
  const { useSiteConfigStore } = await import('./stores/siteConfig')
  const backendStore = useBackendStore()
  const siteConfigStore = useSiteConfigStore()

  await backendStore.initialize()
  await siteConfigStore.fetchConfig()

  next()
})

// Create Pinia instance
const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)
pinia.use(SiteTitleWatcherPlugin)

const app = createApp(App)

// Register Buefy components
app.use(Navbar);
app.use(Menu);
app.use(Sidebar);
app.use(Snackbar);
app.use(Modal);
app.use(Icon);
app.use(Carousel);
app.use(Switch);
app.use(Dialog);
app.use(Notification);
app.use(Tabs);
app.use(Table);
app.use(Button);
app.use(Tooltip);
app.use(Select);
app.use(Input);
app.use(Checkbox);
app.use(Dropdown);
app.use(Field);
app.use(Upload);
app.use(Loading);

// Add $mq global property (simple media query implementation)
app.config.globalProperties.$mq = 'lg'; // Default to desktop
if (typeof window !== 'undefined') {
  const updateMq = () => {
    const width = window.innerWidth;
    if (width < 480) app.config.globalProperties.$mq = 'xs';
    else if (width < 1024) app.config.globalProperties.$mq = 'sm';
    else if (width < 1200) app.config.globalProperties.$mq = 'md';
    else app.config.globalProperties.$mq = 'lg';
  };
  updateMq();
  window.addEventListener('resize', updateMq);
}

// Add simple observe-visibility directive
app.directive('observe-visibility', {
  mounted(el, binding) {
    if (typeof IntersectionObserver !== 'undefined') {
      const observer = new IntersectionObserver((entries) => {
        const isVisible = entries[0].isIntersecting;
        if (typeof binding.value === 'function') {
          binding.value(isVisible);
        } else if (binding.value && typeof binding.value.callback === 'function') {
          binding.value.callback(isVisible);
        }
      }, binding.value?.options || {});
      observer.observe(el);
      el._visibilityObserver = observer;
    }
  },
  unmounted(el) {
    if (el._visibilityObserver) {
      el._visibilityObserver.disconnect();
      delete el._visibilityObserver;
    }
  }
});

// Register icons
registerIcons(app);

// Register router and Pinia
app.use(pinia)
app.use(router)

app.mount('#app')
